import { Button } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";
import { useSelector } from "react-redux";

import { PlusIcon } from "@nens/ranalib";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch } from "../App";
import DurationInput from "../components/DurationInput";
import NumberInput from "../components/NumberInput";
import { checkPermission } from "../state/appState";
import { ExistingWindConstant, getCurrentWind, getExistingWind } from "../state/existingEvents";
import { remindUserToPressPlay } from "../state/mainGui";
import { getSimulation, getSimulationStatus } from "../state/simulation";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import {
  buttonRibbonStyle,
  menuExistingItemCard,
  menuExistingItemCardRow,
} from "../styles/styleComposition";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";
import { durationInputToSeconds, secondsToDuration } from "../utils/timeConverter";
import { timeConverter } from "../utils/timeConverter";
import { beaufortToMeterPerSeconds, meterPerSecondsToBeaufort } from "../utils/windUtils";

import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import ExistingItemsContainer from "./ExistingItemsContainer";
import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";
import menuStyles from "./Menu.module.css";

const arrowDirectionToWindDirectionOrBack = (degrees: number) => {
  return (degrees + 180) % 360;
};

const WindMenu: React.FC<ReturnType<typeof mapDispatchToProps>> = ({ remindUserToPressPlay }) => {
  // lateron used to show all existing wind
  const existingList = useSelector(getExistingWind);

  const [windStrength, setWindStrength] = useState("6"); // 6 beaufort / windkracht 6
  const [windDirection, setWindDirection] = useState("315"); // northwest 360-45
  const [windDuration, setWindDuration] = useState("1:00"); // 1 hour
  const [currentWindStrength, setCurrentWindStrength] = useState("6"); // 6 beaufort / windkracht 6
  const [currentWindDirection, setCurrentWindDirection] = useState("315"); // northwest 360-45
  const [currentWindDuration, setCurrentWindDuration] = useState("1:00"); // northwest 360-45
  const [currentWindEditMode, setCurrentWindEditMode] = useState(false);
  const permission = useSelector(checkPermission)("editWind");
  const intl = useIntl();

  const disableWholeForm = permission.allowed === false;
  const disablesWholeFormErrorMessage =
    permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl);

  const v3Api = getV3Api();
  const simulation = useSelector(getSimulation);
  const simulationStatus = useSelector(getSimulationStatus);
  const getOffset = (): number => {
    return simulationStatus.time || 0;
  };
  const maybeCurrentWind = useSelector(getCurrentWind)(getOffset());

  if (maybeCurrentWind && !currentWindEditMode) {
    const currentbeaufort =
      meterPerSecondsToBeaufort((maybeCurrentWind as ExistingWindConstant).strength) + "";
    if (currentWindStrength !== currentbeaufort) {
      setCurrentWindStrength(currentbeaufort);
    }
    const currentDegrees = (maybeCurrentWind as ExistingWindConstant).direction + "";
    if (currentWindDirection !== currentDegrees) {
      setCurrentWindDirection(currentDegrees);
    }
    const currentDurationInSeconds =
      (maybeCurrentWind as ExistingWindConstant).end -
        (maybeCurrentWind as ExistingWindConstant).start || 0;
    const currentDuration = secondsToDuration(currentDurationInSeconds);
    if (currentWindDuration !== currentDuration + "") {
      setCurrentWindDuration(currentDuration + "");
    }
  }

  if (!simulation) return null;

  return (
    <div className={`${CollapsableSidebarStyles.LeftMenu}`}>
      <LeftMenuHeader>
        <FormattedMessage id="windMenu.header" defaultMessage="Wind" />
      </LeftMenuHeader>
      <div
        className={`${menuStyles.SubMenu} ${menuStyles.SubMenuStaticContent} ${menuStyles.List}`}
        style={{ padding: "0 8px 0 8px" }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <FormLabel>
              <FormattedMessage id="newWindMenu.strength" defaultMessage="Strength" />
            </FormLabel>
            <NumberInput
              value={maybeCurrentWind ? currentWindStrength : windStrength}
              unit={
                <FormattedMessage id="newWindMenu.strength_beaufort" defaultMessage="Beaufort" />
              }
              setValue={(value: string) =>
                maybeCurrentWind ? setCurrentWindStrength(value) : setWindStrength(value)
              }
              min={0}
              max={12}
              required
              disabled={!currentWindEditMode}
            />
            <FormLabel>
              <FormattedMessage id="newWindMenu.direction" defaultMessage="Direction" />
            </FormLabel>
            <NumberInput
              value={maybeCurrentWind ? currentWindDirection : windDirection}
              unit={
                <FormattedMessage id="newWindMenu.direction_degrees" defaultMessage="Degrees" />
              }
              setValue={(value: string) =>
                maybeCurrentWind ? setCurrentWindDirection(value) : setWindDirection(value)
              }
              min={0}
              max={360}
              required
              disabled={!currentWindEditMode}
            />
            <FormLabel
              title={
                maybeCurrentWind && currentWindEditMode
                  ? intl.formatMessage({
                      id: "newwindMenu.fromnow_duration_label_tooltip",
                      defaultMessage: "Reset duration starting from now",
                    })
                  : intl.formatMessage({
                      id: "newwindMenu.total_duration_label_tooltip",
                      defaultMessage: "Total duration from start to end",
                    })
              }
            >
              {maybeCurrentWind && currentWindEditMode ? (
                <FormattedMessage
                  id="newwindMenu.fromnow_duration_label"
                  defaultMessage="Reset duration"
                />
              ) : (
                <FormattedMessage
                  id="newwindMenu.total_duration_label"
                  defaultMessage="Total duration"
                />
              )}
            </FormLabel>

            <DurationInput
              value={maybeCurrentWind ? currentWindDuration : windDuration}
              setValue={maybeCurrentWind ? setCurrentWindDuration : setWindDuration}
              disabled={!currentWindEditMode}
            />
            <div className={`${menuStyles.ListItem} `}>
              <div
                style={{
                  width: "100%",
                  position: "relative",
                  margin: "10px",
                }}
              >
                <img
                  style={{
                    width: "100%",
                  }}
                  src="./assets/images/wind_arrow-raster2.png"
                  alt="windarrow"
                  onClick={(event) => {
                    const target = event.target as HTMLImageElement;
                    const halfImageHeigt = target.height / 2;
                    const halfImageWidth = target.width / 2;
                    const rect = target.getBoundingClientRect();
                    const x = event.clientX - rect.left; //x position within the element.
                    const y = event.clientY - rect.top; //y position within the element.
                    const xOffset = x - halfImageWidth;
                    const yOffset = y - halfImageHeigt;
                    const newDegrees = Math.round(
                      (Math.atan2(xOffset, -yOffset) * 360) / (2 * Math.PI)
                    );
                    let finalDegrees = newDegrees;
                    if (finalDegrees < 0) {
                      finalDegrees = 360 + finalDegrees;
                    }

                    if (maybeCurrentWind) {
                      setCurrentWindDirection(
                        arrowDirectionToWindDirectionOrBack(finalDegrees) + ""
                      );
                    } else {
                      setWindDirection(arrowDirectionToWindDirectionOrBack(finalDegrees) + "");
                    }
                  }}
                />
                <img
                  style={{
                    width: "100%",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    transform: maybeCurrentWind
                      ? `rotate(${
                          arrowDirectionToWindDirectionOrBack(parseInt(currentWindDirection, 10)) ||
                          0
                        }deg)`
                      : `rotate(${
                          arrowDirectionToWindDirectionOrBack(parseInt(windDirection, 10)) || 0
                        }deg)`,
                    pointerEvents: "none",
                  }}
                  src="./assets/images/wind_arrow.svg"
                  alt="windarrow"
                />
              </div>
            </div>

            {maybeCurrentWind && !currentWindEditMode ? (
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setCurrentWindEditMode(true);
                  }}
                >
                  <FormattedMessage id="newwindMenu.button_edit" defaultMessage="Edit" />
                </Button>
              </div>
            ) : null}
            {maybeCurrentWind && currentWindEditMode ? (
              <>
                <div className={buttonRibbonStyle}>
                  <Button
                    variant="secondary"
                    onClick={async (e) => {
                      const target = e.target as HTMLButtonElement;
                      if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                        // stop existing wind from now
                        const newDuration =
                          getOffset() - (maybeCurrentWind as ExistingWindConstant).start;
                        const windStopSimulationId = simulation?.id;
                        if (windStopSimulationId === undefined) return;
                        await v3Api.simulationsEventsWindConstantPartialUpdate({
                          simulationPk: windStopSimulationId,
                          id: (maybeCurrentWind as ExistingWindConstant).id,
                          //@ts-ignore
                          data: {
                            duration: newDuration <= 0 ? 1 : newDuration,
                          },
                        });
                        setCurrentWindEditMode(false);
                        remindUserToPressPlay();
                      }
                    }}
                    marginRight="4px"
                  >
                    <FormattedMessage
                      id="newwindMenu.button_stop_wind"
                      defaultMessage="Stop wind"
                    />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={(e) => {
                      const target = e.target as HTMLButtonElement;
                      if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                        // stop existing wind from now
                        const newDuration =
                          getOffset() - (maybeCurrentWind as ExistingWindConstant).start;
                        const windChangeSimulationId = simulation?.id;
                        if (windChangeSimulationId === undefined) return;
                        v3Api
                          .simulationsEventsWindConstantPartialUpdate({
                            simulationPk: windChangeSimulationId,
                            id: (maybeCurrentWind as ExistingWindConstant).id,
                            //@ts-ignore
                            data: {
                              duration: newDuration <= 0 ? 1 : newDuration,
                            },
                          })
                          .then(() => {
                            // create new wind
                            v3Api
                              .simulationsEventsWindConstantCreate({
                                simulationPk: windChangeSimulationId,
                                data: {
                                  offset: getOffset(),
                                  duration: durationInputToSeconds(currentWindDuration),
                                  speedValue:
                                    beaufortToMeterPerSeconds(parseInt(currentWindStrength, 10)) ||
                                    0,
                                  directionValue: parseInt(currentWindDirection, 10) || 0,
                                },
                              })
                              .then(() => {
                                // set editmode back to false
                                setCurrentWindEditMode(false);
                              });
                          });

                        remindUserToPressPlay();
                      }
                    }}
                  >
                    <FormattedMessage id="newwindMenu.button_update" defaultMessage="Update" />
                  </Button>
                </div>
                <div className={buttonRibbonStyle}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCurrentWindEditMode(false);
                    }}
                  >
                    <FormattedMessage id="newwindMenu.button_cancel" defaultMessage="Cancel" />
                  </Button>
                </div>
              </>
            ) : null}
            {!maybeCurrentWind ? (
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  leftIcon={<PlusIcon />}
                  onClick={(e) => {
                    const target = e.target as HTMLButtonElement;
                    if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                      const windCreateSimulationId = simulation?.id;
                      if (windCreateSimulationId === undefined) return;
                      v3Api.simulationsEventsWindConstantCreate({
                        simulationPk: windCreateSimulationId,
                        data: {
                          offset: getOffset(),
                          duration: durationInputToSeconds(windDuration),
                          speedValue: beaufortToMeterPerSeconds(parseInt(windStrength, 10)) || 0,
                          directionValue: parseInt(windDirection, 10) || 0,
                        },
                      });
                      remindUserToPressPlay();
                    }
                  }}
                >
                  <FormattedMessage id="newwindMenu.button_create" defaultMessage="Create" />
                </Button>
              </div>
            ) : null}

            <div className={`${menuStyles.ListItem} `}>
              {disablesWholeFormErrorMessage ? (
                <span className={ErrorMessageStyles.ErrorMessageStyle}>
                  {disablesWholeFormErrorMessage}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
      {
        <ExistingItemsContainer>
          {existingList &&
            existingList
              .filter((item) => (item as ExistingWindConstant).strength)
              .map((existingItem) => {
                return (
                  <div className={menuExistingItemCard} key={existingItem.uid}>
                    <div className={menuExistingItemCardRow}>
                      <span>
                        <FormattedMessage
                          id="existingWindMenu.strength"
                          defaultMessage="Strength"
                        />
                      </span>

                      <span>
                        {meterPerSecondsToBeaufort((existingItem as ExistingWindConstant).strength)}{" "}
                        <FormattedMessage
                          id="existingWindMenu.strength_beaufort"
                          defaultMessage="Beaufort"
                        />
                      </span>
                    </div>

                    <div className={menuExistingItemCardRow}>
                      <span>
                        <FormattedMessage
                          id="existingWindMenu.direction"
                          defaultMessage="Direction"
                        />
                      </span>
                      <span>
                        {(existingItem as ExistingWindConstant).direction}{" "}
                        <FormattedMessage
                          id="existingWindMenu.unit_degrees"
                          defaultMessage="Degrees"
                        />
                      </span>
                    </div>
                    <div className={menuExistingItemCardRow}>
                      <span>
                        <FormattedMessage id="existingWindMenu.start" defaultMessage="Start" />
                      </span>
                      <span>{timeConverter((existingItem as ExistingWindConstant).start)} </span>
                    </div>
                    <div className={menuExistingItemCardRow}>
                      <span>
                        <FormattedMessage id="existingWindMenu.end" defaultMessage="End" />
                      </span>
                      <span>{timeConverter((existingItem as ExistingWindConstant).end)} </span>
                    </div>
                  </div>
                );
              })}
        </ExistingItemsContainer>
      }
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    remindUserToPressPlay: () => dispatch(remindUserToPressPlay()),
  };
};

export default connect(null, mapDispatchToProps)(WindMenu);
